import { useMemo, useState } from "react";
import { updateAccount } from "wasp/client/operations";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import { getTradePhase } from "./tradePhase";

interface PromoteToFundedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  initialBalance: number;
  /** Trades of the account, each with an ISO date string — used for the phase-split preview */
  trades: { date: string }[];
  /** Called after a successful promotion (parent typically refetches or reloads) */
  onPromoted: () => void;
}

/** Convert a Date to a `datetime-local` input value in LOCAL time: `YYYY-MM-DDTHH:mm`. */
function toDatetimeLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export default function PromoteToFundedModal({
  open,
  onOpenChange,
  accountId,
  initialBalance,
  trades,
  onPromoted,
}: PromoteToFundedModalProps) {
  const t = useTranslation();
  const { toast } = useToast();

  const [fundedDate, setFundedDate] = useState(() => toDatetimeLocal(new Date()));
  const [startingBalance, setStartingBalance] = useState(() => String(initialBalance));
  const [activationFee, setActivationFee] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { challengeCount, fundedCount } = useMemo(() => {
    const parsed = new Date(fundedDate);
    if (isNaN(parsed.getTime())) {
      return { challengeCount: 0, fundedCount: 0 };
    }
    let challenge = 0;
    for (const tr of trades) {
      const phase = getTradePhase(
        { date: new Date(tr.date) },
        { status: "pa", fundedAt: parsed },
      );
      if (phase === "challenge") challenge += 1;
    }
    return { challengeCount: challenge, fundedCount: trades.length - challenge };
  }, [fundedDate, trades]);

  const handleConfirm = async () => {
    const parsedBalance = Number(startingBalance);
    if (!isFinite(parsedBalance) || parsedBalance <= 0) {
      toast({
        title: t.tradingAccounts.promoteBalanceInvalid,
        variant: "destructive",
      });
      return;
    }

    const parsedDate = new Date(fundedDate);

    setSubmitting(true);
    try {
      await updateAccount({
        id: accountId,
        status: "pa",
        fundedAt: parsedDate.toISOString(),
        fundedStartingBalance: parsedBalance,
        ...(activationFee.trim() !== "" ? { activationFee: Number(activationFee) } : {}),
      });
      toast({ title: t.tradingAccounts.promoteSuccess });
      onOpenChange(false);
      onPromoted();
    } catch (err: any) {
      toast({
        title: t.tradingAccounts.error,
        description: err?.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const previewText = t.tradingAccounts.promotePreview
    .replace("{challenge}", String(challengeCount))
    .replace("{funded}", String(fundedCount));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.tradingAccounts.promoteToFundedTitle}</DialogTitle>
          <DialogDescription>
            {t.tradingAccounts.promoteToFundedDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t.tradingAccounts.fundedDateLabel}</Label>
            <Input
              type="datetime-local"
              value={fundedDate}
              onChange={(e) => setFundedDate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t.tradingAccounts.fundedStartingBalanceLabel}</Label>
            <Input
              type="number"
              value={startingBalance}
              onChange={(e) => setStartingBalance(e.target.value)}
              min={0}
              step={0.01}
            />
            <p className="text-xs text-muted-foreground">
              {t.tradingAccounts.fundedStartingBalanceHint}
            </p>
          </div>

          <div className="space-y-2">
            <Label>{t.tradingAccounts.activationFeeOptionalLabel}</Label>
            <Input
              type="number"
              value={activationFee}
              onChange={(e) => setActivationFee(e.target.value)}
              min={0}
              step={0.01}
            />
          </div>

          {trades.length > 0 && (
            <p className="text-xs text-muted-foreground">{previewText}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t.tradingAccounts.promoteCancel}
          </Button>
          <Button onClick={handleConfirm} disabled={submitting}>
            {t.tradingAccounts.promoteConfirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
